/**
 * Navigation friction: the one struggle signal the server cannot see.
 *
 * The audit log knows every request that failed or ran slow, which covers most
 * of what "users are struggling" means. It is blind to one thing: a user who
 * navigates somewhere, finds it is not what they wanted, and comes straight
 * back. Nothing failed. Nothing was slow. The screen was simply mislabelled,
 * or the thing they wanted was somewhere else.
 *
 * That round trip — A, then B, then back to A, quickly — is a wayfinding
 * failure, and it is the closest honest substitute for watching someone look
 * lost. Session recording is off and staying off (PHI; see config/posthog.js),
 * so this is measured as a shape in the route history rather than observed.
 *
 * Routes are sanitized through sanitizeAnalyticsUrl before capture, so what
 * leaves the browser is a route shape and never a patient id.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { sanitizeAnalyticsUrl } from './posthog';
import { EVENTS, captureEvent } from './analyticsEvents';

/**
 * A return trip slower than this is not confusion, it is just work: the user
 * went to B, did something there, and came back on purpose.
 */
export const BACKTRACK_WINDOW_SECONDS = 30;

/** Enough history to see an A→B→A shape; more would only cost memory. */
const TRAIL_LENGTH = 3;

/**
 * Append a visit and report a backtrack if this one completes an A→B→A shape
 * inside the window.
 *
 * Pure, and returns a NEW trail rather than mutating the one passed in, so the
 * detection logic can be tested without a router or a clock.
 *
 * @param {Array<{route: string, at: number}>} trail most recent visit last
 * @param {{route: string, at: number}} visit
 * @returns {{trail: Array<{route: string, at: number}>, backtrack: {fromRoute: string, toRoute: string, seconds: number}|null}}
 */
export function recordVisit(trail, visit) {
  const next = [...trail, visit].slice(-TRAIL_LENGTH);

  // Ignore a re-render that re-reports the route the user is already on.
  const previous = trail[trail.length - 1];
  if (previous && previous.route === visit.route) {
    return { trail, backtrack: null };
  }

  if (next.length < TRAIL_LENGTH) return { trail: next, backtrack: null };

  const [origin, detour, arrival] = next;
  if (origin.route !== arrival.route) return { trail: next, backtrack: null };

  const seconds = (arrival.at - detour.at) / 1000;
  if (seconds > BACKTRACK_WINDOW_SECONDS || seconds < 0) return { trail: next, backtrack: null };

  return {
    trail: next,
    backtrack: {
      fromRoute: detour.route,
      toRoute: arrival.route,
      seconds: Math.round(seconds),
    },
  };
}

/**
 * Watch the router and report backtracks.
 *
 * Mounted once, in the admin shell. Capture failures are swallowed by
 * captureEvent: analytics must never be able to break navigation.
 */
export function useNavigationFrictionTracking() {
  const location = useLocation();
  const trailRef = useRef([]);

  useEffect(() => {
    const route = sanitizeAnalyticsUrl(location.pathname);
    const { trail, backtrack } = recordVisit(trailRef.current, { route, at: Date.now() });
    trailRef.current = trail;

    if (backtrack) {
      captureEvent(EVENTS.NAVIGATION_BACKTRACKED, backtrack);
    }
  }, [location.pathname]);
}
